import React, { useState, useEffect } from 'react';
import './ApiRateLimitManager.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safepumback.civicstech.name.ng/api';

const ApiRateLimitManager = () => {
  const [limits, setLimits] = useState({
    solana_rpc: 60,
    jupiter_api: 120,
    token_info: 100,
    price_updates: 30
  });

  const [refreshIntervals, setRefreshIntervals] = useState({
    wallet_refresh_interval: 120,
    token_refresh_interval: 300
  });

  const [usage, setUsage] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchCurrentLimits();
    fetchRefreshIntervals();
    fetchUsageStats();

    // Refresh usage stats every 30 seconds
    const interval = setInterval(fetchUsageStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const getAuthHeaders = () => {
    const tokens = JSON.parse(localStorage.getItem('tokens') || '{}');
    return {
      'Authorization': `Bearer ${tokens.access_token || ''}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchCurrentLimits = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/api-limits`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        if (data.data?.limits) setLimits(data.data.limits);
        if (data.data?.usage) setUsage(data.data.usage);
      }
    } catch (error) {
      console.error('Error fetching API limits:', error);
    }
  };

  const fetchRefreshIntervals = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/refresh-intervals`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        if (data.data?.intervals) setRefreshIntervals(data.data.intervals);
      }
    } catch (error) {
      console.error('Error fetching refresh intervals:', error);
    }
  };

  const fetchUsageStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/api-usage`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setUsage(data.data);
      }
    } catch (error) {
      console.error('Error fetching usage stats:', error);
    }
  };

  const handleLimitChange = (apiType, value) => {
    setLimits(prev => ({
      ...prev,
      [apiType]: parseInt(value) || 0
    }));
  };

  const handleIntervalChange = (key, value) => {
    setRefreshIntervals(prev => ({
      ...prev,
      [key]: parseInt(value) || 10
    }));
  };

  const updateLimits = async () => {
    setLoading(true);
    setMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/admin/api-limits`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(limits)
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('✅ API rate limits saved successfully');
        fetchUsageStats();
      } else {
        setMessage(`❌ Error: ${data.message}`);
      }
    } catch (error) {
      setMessage(`❌ Error updating limits: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const updateRefreshIntervals = async () => {
    setLoading(true);
    setMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/admin/refresh-intervals`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(refreshIntervals)
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('✅ Refresh intervals saved successfully');
      } else {
        setMessage(`❌ Error: ${data.message}`);
      }
    } catch (error) {
      setMessage(`❌ Error updating intervals: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return '#ff4444';
    if (percentage >= 70) return '#ff8800';
    if (percentage >= 50) return '#ffaa00';
    return '#00aa44';
  };

  const apiTypeLabels = {
    solana_rpc: 'Solana RPC Calls',
    jupiter_api: 'Jupiter API Calls',
    token_info: 'Token Info Calls',
    price_updates: 'Price Update Calls'
  };

  const intervalLabels = {
    wallet_refresh_interval: 'Wallet Data Refresh',
    token_refresh_interval: 'Token Data Refresh'
  };

  const intervalDescriptions = {
    wallet_refresh_interval: 'How often wallet balances are refreshed from Solana (in seconds)',
    token_refresh_interval: 'How often token price/info data is refreshed (in seconds)'
  };

  return (
    <div className="api-rate-limit-manager">
      <div className="rate-limit-header">
        <h3>🚦 API Rate Limit Management</h3>
        <p>Configure API call limits per minute to prevent rate limiting and reduce costs</p>
      </div>

      {message && (
        <div className={`message ${message.includes('✅') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      <div className="rate-limit-grid">
        {Object.keys(limits).map(apiType => (
          <div key={apiType} className="rate-limit-card">
            <div className="card-header">
              <h4>{apiTypeLabels[apiType]}</h4>
            </div>

            <div className="limit-input-group">
              <label>Calls per minute:</label>
              <input
                type="number"
                min="1"
                max="1000"
                value={limits[apiType]}
                onChange={(e) => handleLimitChange(apiType, e.target.value)}
                className="limit-input"
              />
            </div>

            {usage[apiType] && (
              <div className="usage-stats">
                <div className="usage-bar">
                  <div
                    className="usage-fill"
                    style={{
                      width: `${Math.min(usage[apiType].percentage, 100)}%`,
                      backgroundColor: getUsageColor(usage[apiType].percentage)
                    }}
                  />
                </div>
                <div className="usage-text">
                  <span>{usage[apiType].current} / {usage[apiType].limit}</span>
                  <span className="usage-percentage">
                    {usage[apiType].percentage.toFixed(1)}%
                  </span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="rate-limit-actions">
        <button
          onClick={updateLimits}
          disabled={loading}
          className="update-button"
        >
          {loading ? 'Saving...' : '💾 Save Rate Limits'}
        </button>

        <button
          onClick={fetchUsageStats}
          className="refresh-button"
        >
          🔄 Refresh Stats
        </button>
      </div>

      {/* Refresh Intervals Section */}
      <div className="rate-limit-header" style={{ marginTop: '24px' }}>
        <h3>⏱️ Refresh Intervals</h3>
        <p>Control how often the app fetches wallet balances and token data to reduce API usage</p>
      </div>

      <div className="rate-limit-grid">
        {Object.keys(refreshIntervals).map(key => (
          <div key={key} className="rate-limit-card">
            <div className="card-header">
              <h4>{intervalLabels[key]}</h4>
            </div>

            <div className="limit-input-group">
              <label>Interval (seconds):</label>
              <input
                type="number"
                min="10"
                max="3600"
                value={refreshIntervals[key]}
                onChange={(e) => handleIntervalChange(key, e.target.value)}
                className="limit-input"
              />
            </div>

            <div className="interval-description">
              {intervalDescriptions[key]}
            </div>

            <div className="interval-display">
              ≈ {refreshIntervals[key] >= 60
                ? `${Math.floor(refreshIntervals[key] / 60)}m ${refreshIntervals[key] % 60}s`
                : `${refreshIntervals[key]}s`
              }
            </div>
          </div>
        ))}
      </div>

      <div className="rate-limit-actions">
        <button
          onClick={updateRefreshIntervals}
          disabled={loading}
          className="update-button"
        >
          {loading ? 'Saving...' : '💾 Save Refresh Intervals'}
        </button>
      </div>

      <div className="rate-limit-info">
        <h4>💡 Recommendations:</h4>
        <ul>
          <li><strong>Solana RPC:</strong> 60-120 calls/min for normal operation</li>
          <li><strong>Jupiter API:</strong> 100-200 calls/min for active trading</li>
          <li><strong>Token Info:</strong> 50-100 calls/min for token discovery</li>
          <li><strong>Price Updates:</strong> 30-60 calls/min for price monitoring</li>
          <li><strong>Wallet Refresh:</strong> 120-300 seconds to reduce RPC load</li>
          <li><strong>Token Refresh:</strong> 300-600 seconds for less frequent token updates</li>
        </ul>

        <div className="warning-box">
          ⚠️ <strong>Warning:</strong> Setting limits too low may affect system functionality.
          Settings are saved to the database and persist across server restarts.
        </div>
      </div>
    </div>
  );
};

export default ApiRateLimitManager;